import { useMemo, useState, type MouseEvent } from 'react';
import {
  FLOOR_ORDER,
  omCapParts,
  ourCapParts,
  sortDeals,
  type FloorKey,
  type PortalFloorRow,
  type TermsColumn,
  type TermsGroup,
  type TermsRowDeal,
} from './termsTable';
import CapText from './CapText';

/**
 * Every deal on one line (VP8.4) - Joseph's ask after seeing the portal: "a large table that shows
 * all deals' terms laid out in one place".
 *
 * ⛔ EVERY CELL IS A QUOTE. Ask, tenant, our read and status are the contract's own strings; the OM cap is the
 * seller's OM, quoted with the price it prints it at when that is not the ask (VP8.4 round eight); NOI, our cap,
 * building SF, price per SF and the five floors are quoted from ONE of the deal's investment analyses
 * (named under the deal when it has more than one). A cell no document states is EMPTY - never a zero,
 * never a dash a reader could take for a figure. The tally ("3 of 5") is a count of the statuses
 * printed beside it, and the import gate refuses a contract where the two disagree.
 */

const FLOOR_HEAD: Record<FloorKey, string> = {
  cap: 'Going-in cap',
  coc: 'Cash-on-cash',
  irr: 'IRR',
  em: 'Equity multiple',
  dscr: 'DSCR',
};

interface Column {
  id: TermsColumn;
  label: string;
  /** Which way a first click sorts: the largest figure, or the newest date, first. */
  first: 'asc' | 'desc';
  className?: string;
}

/*
  His list, re-ranked by LOOKING at his width (1728px, 2026-09-10): in the order he named them, the five
  floors began past 1,540px - the scoreboard the table exists for sat off the edge until the reader
  scrolled sideways. So the figures lead, the floors follow them, and the long prose columns (our read,
  status, tenant) come after. The deal cell carries its group's colour, so the grouping still reads
  with the status column scrolled away.
*/
const COLUMNS: Column[] = [
  { id: 'deal', label: 'Deal', first: 'asc' },
  { id: 'ask', label: 'Ask', first: 'desc', className: 'portal-terms-fig' },
  { id: 'omCap', label: 'OM cap', first: 'desc', className: 'portal-terms-fig' },
  { id: 'ourCap', label: 'Our cap', first: 'desc', className: 'portal-terms-fig' },
  { id: 'noi', label: 'NOI in place', first: 'desc', className: 'portal-num' },
  { id: 'sf', label: 'Building SF', first: 'desc', className: 'portal-num' },
  { id: 'priceSf', label: 'Price / SF', first: 'desc', className: 'portal-num' },
  { id: 'floors', label: 'Floors passed', first: 'desc', className: 'portal-num' },
  ...FLOOR_ORDER.map(
    (k): Column => ({ id: `floor:${k}`, label: FLOOR_HEAD[k], first: 'desc', className: 'portal-num portal-terms-floor' })
  ),
  { id: 'read', label: 'Our read', first: 'asc' },
  { id: 'status', label: 'Status', first: 'asc' },
  { id: 'tenant', label: 'Tenant', first: 'asc' },
  { id: 'analysed', label: 'Analysis date', first: 'desc', className: 'portal-num' },
];

const STATUS_WORD: Record<string, string> = { PASS: 'pass', FAIL: 'fail' };

/** One floor: the actual the analysis printed, marked pass or fail; "n/a" where it marked the metric n/a. */
const FloorCell = ({ row }: { row: PortalFloorRow | null | undefined }) => {
  const status = (row?.status ?? '').toUpperCase();
  const cls = 'portal-num portal-terms-floor';
  if (!row || !['PASS', 'FAIL', 'N/A'].includes(status)) return <td className={cls} />;
  const title = [row.label, row.threshold ? `floor ${row.threshold}` : null].filter(Boolean).join(' - ') || undefined;
  if (status === 'N/A') {
    return (
      <td className={`${cls} portal-floor-na`} title={title}>
        n/a
      </td>
    );
  }
  const pass = status === 'PASS';
  return (
    <td className={`${cls} ${pass ? 'portal-floor-pass' : 'portal-floor-fail'}`} title={title}>
      <span aria-hidden="true">{pass ? '✓ ' : '✗ '}</span>
      {row.actual ?? STATUS_WORD[status]}
      {row.actual && <span className="portal-sr-only"> {STATUS_WORD[status]}</span>}
    </td>
  );
};

interface Props {
  /** In the order the cards read: by the legend, then as the contract lists them. */
  deals: TermsRowDeal[];
  groups: Record<string, TermsGroup | undefined>;
  /** Names the analysis a row quotes - the page returns one only for a deal with several. */
  sourceNote?: (d: TermsRowDeal) => string | null;
  /** The page's "go to deal": opens a folded group, scrolls, and highlights the card. */
  onGoToDeal?: (dealId: string) => void;
}

export default function PortalTermsTable({ deals, groups, sourceNote, onGoToDeal }: Props) {
  // Status first, the way the cards read - the table opens in the order the rest of the page uses.
  const [sortCol, setSortCol] = useState<TermsColumn>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const rows = useMemo(() => {
    const groupOrder = (g?: string): number | null => {
      const o = g ? groups[g]?.order : undefined;
      return typeof o === 'number' ? o : null;
    };
    return sortDeals(deals, sortCol, sortDir, groupOrder);
  }, [deals, groups, sortCol, sortDir]);

  const onSort = (c: Column) => {
    if (c.id === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(c.id);
      setSortDir(c.first);
    }
  };

  /* A real link to the card, so it still works with no handler (and prints as a name). With one, the
     page's own "go to deal" runs instead: it opens a folded group first, which a bare anchor cannot. */
  const jump = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!onGoToDeal) return;
    e.preventDefault();
    onGoToDeal(id);
  };

  return (
    <div className="portal-table-scroll portal-terms-scroll">
      <table className="portal-table portal-terms" data-testid="portal-terms-table">
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th
                key={c.id}
                scope="col"
                className={c.className}
                aria-sort={sortCol === c.id ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                <button type="button" onClick={() => onSort(c)}>
                  {c.label}
                  {sortCol === c.id ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </button>
                {/* Paper's copy of the heading: the header Chrome repeats on a continuation page drops a button's words. */}
                <span className="portal-terms-head-print" aria-hidden="true">
                  {c.label}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const t = d.terms ?? null;
            const sc = t?.scorecard ?? null;
            const tally =
              sc && typeof sc.passed === 'number' && typeof sc.applicable === 'number'
                ? `${sc.passed} of ${sc.applicable}`
                : '';
            const note = t?.source && sourceNote ? sourceNote(d) : null;
            const group = d.status?.group ? groups[d.status.group] : undefined;
            return (
              <tr key={d.id} data-testid={`portal-terms-row-${d.id}`}>
                <th scope="row" className="portal-terms-deal" style={{ borderLeftColor: group?.mapColor || undefined }}>
                  <a href={`#deal-${d.id}`} onClick={jump(d.id)}>
                    {d.name ?? d.id}
                  </a>
                  {/* Most names already end in the city ("Grocery Outlet - Cathedral City"); a second line
                      saying it again only makes every row taller. */}
                  {d.city && !(d.name ?? '').includes(d.city) && <span className="portal-terms-city">{d.city}</span>}
                  {note && <span className="portal-terms-source">Figures from: {note}</span>}
                </th>
                <td className="portal-terms-fig">{d.ask ?? ''}</td>
                <td className="portal-terms-fig">
                  <CapText parts={omCapParts(d.omCap, d.ask)} />
                </td>
                <td className="portal-terms-fig" title={t?.ourCap?.label || undefined}>
                  <CapText parts={ourCapParts(t?.ourCap, d.ask)} />
                </td>
                <td className="portal-num">{t?.noiInPlace ?? ''}</td>
                <td className="portal-num">{t?.sf ?? ''}</td>
                <td className="portal-num" title={t?.priceSf && t.priceSfAt ? `at ${t.priceSfAt}` : undefined}>
                  {t?.priceSf ?? ''}
                </td>
                <td className="portal-num">{tally}</td>
                {FLOOR_ORDER.map((k) => (
                  <FloorCell key={k} row={sc?.floors?.[k]} />
                ))}
                <td className="portal-terms-long">{d.verdict ?? ''}</td>
                <td className="portal-terms-long">{d.status?.label ?? ''}</td>
                <td className="portal-terms-long">{d.tenant ?? ''}</td>
                <td className="portal-num">{d.analysisDate ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
